import {
	type DiscountCode,
	DiscountCodeListInput,
	type DiscountCodeListQuery,
	DiscountCodeOrigin,
	DiscountCodeStatus,
} from "@cleanbudget/contract/discount-code";
import { IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ButtonLink } from "@/components/button-link";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { formatDate } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { statusTone, useDescribe } from "./-components/describe";
import { OriginBadge } from "./-components/origin-badge";

const codesQuery = (query: DiscountCodeListQuery) => orpc.discountCode.list.queryOptions({ input: query });

/** The list's defaults, left out of the URL so a plain `/discount-code` is the default view. */
const { sort, order, page, pageSize } = DiscountCodeListInput.parse({});

/** Kortingscodes: every code with its status (actief, gepland, verlopen, op), newest first. */
export const Route = createFileRoute("/_admin/discount-code/")({
	validateSearch: DiscountCodeListInput,
	search: { middlewares: [stripSearchParams({ sort, order, page, pageSize })] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) => context.queryClient.ensureQueryData(codesQuery(deps)),
	component: DiscountCodesPage,
});

function DiscountCodesPage() {
	const { t } = useTranslation("discountCode");
	const describe = useDescribe();
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(codesQuery(search));

	const columns: readonly Column<DiscountCode>[] = [
		{
			id: "code",
			header: t("column.code"),
			sortable: true,
			cell: (row) => (
				<span className="flex flex-col items-start gap-1">
					<span className="flex flex-wrap items-center gap-2">
						<Link
							to="/discount-code/$id"
							params={{ id: row.id }}
							className="font-mono font-medium underline-offset-2 hover:underline"
						>
							{row.code}
						</Link>
						<OriginBadge code={row} />
					</span>
					{/* On a phone the other columns are hidden; the kind goes under the code. */}
					<span className="text-xs text-muted-foreground md:hidden">{describe.kind(row)}</span>
				</span>
			),
		},
		{ id: "discount", header: t("column.discount"), secondary: true, cell: (row) => describe.kind(row) },
		{
			id: "endsAt",
			header: t("column.validity"),
			sortable: true,
			secondary: true,
			cell: (row) => <span className="text-muted-foreground">{describe.validity(row)}</span>,
		},
		{ id: "uses", header: t("column.uses"), secondary: true, cell: (row) => describe.uses(row) },
		{
			id: "status",
			header: t("column.status"),
			cell: (row) => <StatusBadge tone={statusTone(row.status)}>{describe.status(row.status)}</StatusBadge>,
		},
		{
			id: "createdAt",
			header: t("column.created"),
			sortable: true,
			secondary: true,
			cell: (row) => <time dateTime={row.createdAt}>{formatDate(row.createdAt)}</time>,
		},
	];

	const filtered = Boolean(search.search) || search.status !== undefined || search.origin !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("title")}
				description={t("description")}
				actions={
					<ButtonLink to="/discount-code/new">
						<IconPlus aria-hidden />
						{t("new.title")}
					</ButtonLink>
				}
			/>
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="status"
						label={t("status.label")}
						value={search.status}
						options={DiscountCodeStatus.options.map((status) => ({
							value: status,
							label: describe.status(status),
						}))}
					/>
					<FilterSelect
						param="origin"
						label={t("origin.label")}
						value={search.origin}
						options={DiscountCodeOrigin.options.map((origin) => ({
							value: origin,
							label: t(`origin.${origin}`),
						}))}
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(row) => row.id}
					sort={search.sort}
					order={search.order}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}
